import ForwardTracker from "../../../../../prop-trackers/forward-tracker.js";
import ValueTracker from "../../../../../prop-trackers/value-tracker.js";
import PropTracker from "../../../../../prop-trackers/prop-tracker.js";
import DictTracker from "../../../../../prop-trackers/dict-tracker.js";
import { Mapper, ValueMapper } from "../../../../../prop-tracker-utils/mapper.js";

const Properties = {
    position: Array.from("xyz"),
    quaternion: Array.from("xyzw"),
    scale: Array.from("xyz"),
};

class ArrayToObjectCoordGenerator extends ForwardTracker {
    constructor(arrayValueTracker, wrapCtx, evtCtx) {
        const mapper = new Mapper(arrayValueTracker, (key) => new ObjectMapper(wrapCtx, evtCtx));
        super(mapper);
    }
}

class ObjectMapper extends ValueMapper {
    constructor(wrapCtx, evtCtx) {
        super();

        this._output = wrapCtx.getWrapper({});
        this._outputTracker = new ValueTracker();

        this._evtCtx = evtCtx;
        this._arraysTracker = new PropTracker();
        this._filterArraysTracker = new DictTracker();
        this._propertyMapper = new Mapper(this._filterArraysTracker, (property) => new PropertyMapper(Properties[property], wrapCtx, evtCtx));

        const needsUpdate = () => this.protected_needsUpdate();
        this._arraysTracker.listenNeedsUpdate(needsUpdate);
        this._propertyMapper.listenNeedsUpdate(needsUpdate);
    }

    setValue(arrayData) {
        if (arrayData) {
            this._arraysTracker.track(arrayData, this._evtCtx.getEvents(arrayData));
            this._outputTracker.setValue(this._output);
        } else {
            this.deleteValue();
        }
    }

    deleteValue() {
        this._arraysTracker.track();
        this._outputTracker.deleteValue();
    }

    protected_startUpdate() {
        this._arraysTracker.updateIfNeeded(({ props }) => {
            for (const { key, newExists, newValue } of props.iterateModified()) {
                if (key in Properties) {
                    if (newExists) {
                        this._filterArraysTracker.setValue(key, newValue);
                    } else {
                        this._filterArraysTracker.deleteValue(key);
                    }
                }
            }
        });

        this._propertyMapper.updateIfNeeded(({ props }) => {
            for (const { key, newExists, newValue } of props.iterateModified()) {
                if (newExists) {
                    this._output[key] = newValue;
                } else {
                    delete this._output[key];
                }
            }
        });

        return this._outputTracker.startUpdate();
    }

    protected_finishUpdate() {
        this._outputTracker.finishUpdate();
    }
}

class PropertyMapper extends ValueMapper {
    constructor(indexToKey, wrapCtx, evtCtx) {
        super();

        this._indexToKey = indexToKey;
        this._evtCtx = evtCtx;

        this._output = wrapCtx.getWrapper({});

        this._arrayTracker = new PropTracker();
        this._outputTracker = new ValueTracker();

        const needsUpdate = () => this.protected_needsUpdate();
        this._arrayTracker.listenNeedsUpdate(needsUpdate);
    }

    setValue(array) {
        if (array) {
            this._arrayTracker.track(array, this._evtCtx.getEvents(array));
            this._outputTracker.setValue(this._output);
        } else {
            this.deleteValue();
        }
    }

    deleteValue() {
        this._arrayTracker.track();
        this._outputTracker.deleteValue();
    }

    protected_startUpdate() {
        this._arrayTracker.update(({ props }) => {
            for (const { key: index, newExists, newValue } of props.iterateModified()) {
                const key = this._indexToKey[index];

                if (newExists) {
                    this._output[key] = newValue;
                } else {
                    delete this._output[key];
                }
            }
        });

        return this._outputTracker.startUpdate();
    }

    protected_finishUpdate() {
        this._outputTracker.finishUpdate();
    }
}

export default ArrayToObjectCoordGenerator;
